import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, Download as DownloadIcon } from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';

const ExcelImport = ({ open, onClose, onImport, title, template }) => {
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            readExcel(selectedFile);
        }
    };

    const readExcel = async (file) => {
        try {
            const buffer = await file.arrayBuffer();
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(buffer);
            const worksheet = workbook.worksheets[0];

            if (!worksheet || worksheet.rowCount <= 1) {
                setError('The uploaded file is empty.');
                setPreviewData([]);
                return;
            }

            // Get headers from the first row
            const headers = [];
            const headerRow = worksheet.getRow(1);
            headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                headers[colNumber] = cell.value != null ? String(cell.value) : '';
            });

            // Build JSON array from remaining rows
            const jsonData = [];
            worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
                if (rowNumber === 1) return; // skip header
                const rowObj = {};
                headers.forEach((header, colNumber) => {
                    if (!header) return;
                    const cell = row.getCell(colNumber);
                    const val = cell.value;
                    // Handle ExcelJS rich text, date, and other types
                    if (val && typeof val === 'object' && val.richText) {
                        rowObj[header] = val.richText.map(r => r.text).join('');
                    } else if (val instanceof Date) {
                        rowObj[header] = val.toISOString().split('T')[0];
                    } else {
                        rowObj[header] = val != null ? String(val) : '';
                    }
                });
                jsonData.push(rowObj);
            });

            if (jsonData.length === 0) {
                setError('The uploaded file is empty.');
                setPreviewData([]);
                return;
            }

            setPreviewData(jsonData);
            setError(null);
        } catch (err) {
            console.error("Excel Read Error:", err);
            setError('Failed to read Excel file. Please ensure it is a valid .xlsx file.');
            setPreviewData([]);
        }
    };

    const handleImport = async () => {
        if (previewData.length === 0) {
            toast.warning('No data to import');
            return;
        }

        setLoading(true);
        try {
            await onImport(previewData);
            handleClose();
        } catch (err) {
            console.error(err);
            // Error is usually handled by the parent callback toast, but we can set local error too
            setError(err.message || 'Import failed');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setPreviewData([]);
        setError(null);
        onClose();
    };

    const handleDownloadTemplate = async () => {
        const workbook = new ExcelJS.Workbook();
        const ws = workbook.addWorksheet('Template');
        const headers = Object.keys(template);
        ws.addRow(headers);
        ws.addRow(headers.map(h => template[h]));
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `${title.replace(/\s+/g, '_')}_Template.xlsx`);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" gutterBottom>
                        1. Download the template to see the required format.<br />
                        2. Fill in the data (ensure headers match).<br />
                        3. Upload the file to preview and import.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadTemplate}
                        >
                            Download Template
                        </Button>
                        <Button
                            variant="contained"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                        >
                            Select Excel File
                            <input
                                type="file"
                                hidden
                                accept=".xlsx, .xls"
                                onChange={handleFileChange}
                            />
                        </Button>
                    </Box>
                    {file && <Typography variant="caption" display="block" sx={{ mt: 1 }}>Selected: {file.name}</Typography>}
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {previewData.length > 0 && (
                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {Object.keys(previewData[0]).map((key) => (
                                        <TableCell key={key} sx={{ fontWeight: 'bold' }}>{key}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {previewData.slice(0, 10).map((row, i) => (
                                    <TableRow key={i}>
                                        {Object.values(row).map((val, idx) => (
                                            <TableCell key={idx}>{val}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {previewData.length > 10 && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                        Showing first 10 of {previewData.length} records...
                    </Typography>
                )}

            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    onClick={handleImport}
                    variant="contained"
                    disabled={previewData.length === 0 || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                >
                    {loading ? 'Importing...' : 'Start Import'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExcelImport;
